import React, { useState } from 'react';
import { useProfile } from '../context/ProfileContext';
import { createProfile } from '../services/profilesService';
import './comic.css';

/**
 * First-run gate. Blocks the app until the Manager has recruited at least one
 * Hero (non-archived profile). Once one exists, renders children pass-through.
 *
 * ownerUserId is passed in (not read from context) to match how App.js already
 * threads user.uid down to RoutinePicker/ActiveMission.
 */
const ProfileGate = ({ ownerUserId, children }) => {
  const { profiles, activeProfile, loading, refreshProfiles } = useProfile();

  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // One-time "How Pumpous Works" explainer, shown only in the same session
  // where the Manager creates their very first Hero (set true right after a
  // successful handleCreate below, never persisted). A returning Manager who
  // already has a profile from a prior session skips straight to children,
  // same as before this existed — no localStorage flag needed since the gate
  // itself already won't re-render once hasProfile is true.
  const [justOnboarded, setJustOnboarded] = useState(false);

  // Wait for the initial profile load before deciding gate vs. pass-through,
  // so we never flash the "first Hero" screen at a Manager who already has
  // one. Scoped to `profiles.length === 0` (genuinely nothing loaded yet),
  // not just `loading` alone -- `refreshProfiles()` (called after any
  // create/rename/archive, e.g. GuidedTour.jsx's temporary demo Hero)
  // flips `loading` true again on every refresh, not only the first one,
  // and this used to unconditionally fall through to this same blocking
  // screen every time, remounting the entire authenticated tree below it
  // (everything in `children`) for the duration of each refresh. Real bug,
  // not just a cosmetic flash: caught when GuidedTour.jsx's own state (its
  // current step, a ref tracking an in-progress demo Mission) was being
  // silently wiped mid-tour by its own `refreshProfiles()` call after
  // creating a practice Hero -- a stale `profiles.length === 0` check from
  // before anything had ever loaded doesn't apply to a REFRESH of already-
  // loaded data, since `profiles` still holds the previous (correct) array
  // the whole time a refresh is in flight (React only replaces it once the
  // new data arrives, never clears it first) -- so gating on `profiles.
  // length === 0` lets a background refresh update profiles/activeProfile
  // in place without unmounting anything below this gate.
  if (loading && profiles.length === 0) {
    return (
      <div className="comic-panel comic-panel--outer-active" style={{ textAlign: "center" }}>
        <p className="comic-empty">Loading Heroes...</p>
      </div>
    );
  }

  const hasProfile =
    activeProfile !== null ||
    profiles.filter((p) => p.isActive !== false).length > 0;

  if (hasProfile) {
    if (justOnboarded) {
      return (
        <div className="comic-panel comic-panel--outer-active">
          <h2 className="comic-title" style={{ fontSize: "26px" }}>
            How Pumpous Works
          </h2>
          <p className="comic-subtitle">
            <strong>Heroes</strong> are the athletes you train — you, or
            teammates you're coaching. Switch between them any time from the
            header.
          </p>
          <p className="comic-subtitle">
            A <strong>Mission</strong> is a workout: pick a routine, log your
            sets as you go, and end it when you're done.
          </p>
          <p className="comic-subtitle">
            Beat your best weight on a lift and it's flagged as a{" "}
            <strong>PR</strong> automatically — no extra work.
          </p>
          <button
            type="button"
            onClick={() => setJustOnboarded(false)}
            className="comic-btn comic-btn--go"
          >
            Let's Go, Hero
          </button>
        </div>
      );
    }
    return children;
  }

  const handleCreate = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    setError(null);
    const result = await createProfile(ownerUserId, trimmed);
    if (result.success) {
      setName("");
      setJustOnboarded(true);
      // Re-fetch so the gate re-evaluates and lets the real app through.
      await refreshProfiles();
    } else {
      setError(
        typeof result.error === "string"
          ? result.error
          : "Couldn't recruit your Hero. Try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="comic-panel comic-panel--outer-active">
      <h2 className="comic-title" style={{ fontSize: "26px" }}>
        Name Your First Hero
      </h2>
      <p className="comic-subtitle">
        Every mission needs a Hero. Heroes are the athletes you train — start
        with yourself, or add teammates later. Give this one a name to begin.
      </p>

      <form onSubmit={handleCreate}>
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            type="text"
            placeholder="Hero name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="comic-input"
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="comic-btn comic-btn--go"
        >
          {submitting ? "Recruiting..." : "Recruit Hero"}
        </button>

        {error && <div className="comic-alert-bubble">{error}</div>}
      </form>
    </div>
  );
};

export default ProfileGate;
